import api from './api';
import { BASE_URL } from '../apiConfig';
import {
  StandaloneUnitDto,
  PaginatedStandaloneUnits,
  StandaloneUnitsResponse,
  MyUnitCodeDto,
  StandaloneUnitDetails,
  StandaloneUnitDetailsResponse,
  StandaloneUnitLessonDto,
} from '../types/unit';

export function normalizeStandaloneUnit(raw: Record<string, unknown>): StandaloneUnitDto {
  return {
    id: Number(raw.id ?? raw.Id ?? 0),
    unitName: String(raw.unitName ?? raw.UnitName ?? ''),
    titel: String(raw.titel ?? raw.Titel ?? ''),
    unitTitle: String(raw.unitTitle ?? raw.UnitTitle ?? raw.titel ?? raw.Titel ?? ''),
    price: Number(raw.price ?? raw.Price ?? 0),
    imagePath: (raw.imagePath ?? raw.ImagePath ?? null) as string | null,
    teacherId: (raw.teacherId ?? raw.TeacherId ?? null) as string | null,
    active: Boolean(raw.active ?? raw.Active ?? true),
    order: Number(raw.order ?? raw.Order ?? 0),
    isIndividual: Boolean(raw.isIndividual ?? raw.IsIndividual ?? false),
    creationDate: (raw.creationDate ?? raw.CreationDate ?? undefined) as string | undefined,
  };
}

function normalizePaginatedUnits(raw: Record<string, unknown>): PaginatedStandaloneUnits {
  const dataRaw = (raw.data ?? raw.Data ?? []) as Record<string, unknown>[];
  return {
    currentPage: Number(raw.currentPage ?? raw.CurrentPage ?? 1),
    pageSize: Number(raw.pageSize ?? raw.PageSize ?? 10),
    totalPages: Number(raw.totalPages ?? raw.TotalPages ?? 1),
    totalCount: Number(raw.totalCount ?? raw.TotalCount ?? 0),
    hasPreviousPage: Boolean(raw.hasPreviousPage ?? raw.HasPreviousPage ?? false),
    hasNextPage: Boolean(raw.hasNextPage ?? raw.HasNextPage ?? false),
    data: Array.isArray(dataRaw) ? dataRaw.map(normalizeStandaloneUnit) : [],
  };
}

export function getStandaloneUnitImageUrl(unit: StandaloneUnitDto): string {
  if (!unit.imagePath) return '/default-course.jpg';
  if (unit.imagePath.startsWith('http')) return unit.imagePath;
  const path = unit.imagePath.startsWith('/') ? unit.imagePath.slice(1) : unit.imagePath;
  return `${BASE_URL}${path}`;
}

function normalizeStandaloneLesson(raw: Record<string, unknown>): StandaloneUnitLessonDto {
  return {
    id: Number(raw.id ?? raw.Id ?? 0),
    lessonName: String(raw.lessonName ?? raw.LessonName ?? ''),
    titel: String(raw.titel ?? raw.Titel ?? ''),
    order: Number(raw.order ?? raw.Order ?? 0),
    active: Boolean(raw.active ?? raw.Active ?? true),
    type: String(raw.type ?? raw.Type ?? 'Video'),
  };
}

function normalizeStandaloneUnitDetails(raw: Record<string, unknown>): StandaloneUnitDetails {
  const lessonsRaw = (raw.lessons ?? raw.Lessons ?? []) as Record<string, unknown>[];
  
  const normalized = {
    id: Number(raw.id ?? raw.Id ?? 0),
    unitName: String(raw.unitName ?? raw.UnitName ?? ''),
    titel: String(raw.titel ?? raw.Titel ?? ''),
    price: Number(raw.price ?? raw.Price ?? 0),
    imagePath: (raw.imagePath ?? raw.ImagePath ?? null) as string | null,
    teacherId: (raw.teacherId ?? raw.TeacherId ?? null) as string | null,
    active: Boolean(raw.active ?? raw.Active ?? true),
    order: Number(raw.order ?? raw.Order ?? 0),
    creationDate: (raw.creationDate ?? raw.CreationDate ?? undefined) as string | undefined,
    lessons: Array.isArray(lessonsRaw)
      ? lessonsRaw.map(normalizeStandaloneLesson).filter((l) => l.active)
      : [],
  };
  
  console.log('Normalizing unit details:', {
    rawId: raw.id ?? raw.Id,
    normalizedId: normalized.id,
    rawUnitName: raw.unitName ?? raw.UnitName,
    rawTitel: raw.titel ?? raw.Titel,
    rawActive: raw.active ?? raw.Active,
    lessonsCount: normalized.lessons.length,
    activeLessonsCount: normalized.lessons.length,
    totalLessonsInRaw: lessonsRaw.length
  });
  
  return normalized;
}

export function standaloneDetailsToBrowseUnit(details: StandaloneUnitDetails): StandaloneUnitDto {
  return {
    id: details.id,
    unitName: details.unitName,
    titel: details.titel,
    unitTitle: details.titel,
    price: details.price,
    imagePath: details.imagePath,
    teacherId: details.teacherId,
    active: details.active,
    order: details.order,
    isIndividual: true,
    creationDate: details.creationDate,
  };
}

export const unitBrowseService = {
  async getStandaloneUnitById(
    unitId: number
  ): Promise<{ success: true; data: StandaloneUnitDetails } | { success: false; error: string }> {
    try {
      const response = await api.get<StandaloneUnitDetailsResponse>(
        `/api/Unit/${unitId}/basic-details`
      );
      const body = response.data;
      
      // Add detailed logging for debugging
      console.log('API Response for unit', unitId, ':', {
        httpStatus: response.status,
        bodySuccess: body.success,
        hasData: !!body.data,
        message: body.message,
        fullBody: body
      });
      
      if (!body.success || !body.data) {
        const errorMsg = body.message || 'الحصة غير متاحة';
        console.warn('Unit fetch failed:', { unitId, success: body.success, hasData: !!body.data, message: errorMsg });
        return { success: false, error: errorMsg };
      }
      const payload = body.data as unknown as Record<string, unknown>;
      const normalized = normalizeStandaloneUnitDetails(payload);
      console.log('Normalized unit data:', normalized);
      return { success: true, data: normalized };
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string }; status?: number } };
      console.error('Unit fetch error:', {
        unitId,
        status: err.response?.status,
        message: err.response?.data?.message,
        error
      });
      return {
        success: false,
        error: err.response?.data?.message || 'تعذر تحميل تفاصيل الحصة',
      };
    }
  },

  async getUnitsWithoutCourse(
    options?: { teacherId?: string; pageNumber?: number; pageSize?: number }
  ): Promise<{ success: true; data: PaginatedStandaloneUnits } | { success: false; error: string }> {
    const { teacherId, pageNumber = 1, pageSize = 12 } = options ?? {};
    try {
      const params: Record<string, string | number> = { pageNumber, pageSize };
      if (teacherId) params.teacherId = teacherId;

      const response = await api.get<StandaloneUnitsResponse>('/api/Unit/without-course', { params });
      const body = response.data;

      if (!body.success) {
        return { success: false, error: body.message || 'لا توجد حصص متاحة' };
      }

      const payload = body.data as unknown as Record<string, unknown>;
      return { success: true, data: normalizePaginatedUnits(payload) };
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      const message =
        err.response?.data?.message || 'تعذر تحميل الحصص. حاول مرة أخرى.';
      return { success: false, error: message };
    }
  },
};

export function normalizeMyUnitCode(raw: Record<string, unknown>): MyUnitCodeDto {
  return {
    unitId: Number(raw.unitId ?? raw.UnitId ?? 0),
    unitName: String(raw.unitName ?? raw.UnitName ?? ''),
    unitTitle: String(raw.unitTitle ?? raw.UnitTitle ?? ''),
    courseId: String(raw.courseId ?? raw.CourseId ?? ''),
    courseName: String(raw.courseName ?? raw.CourseName ?? ''),
    grade: String(raw.grade ?? raw.Grade ?? ''),
    term: String(raw.term ?? raw.Term ?? ''),
    active: Boolean(raw.active ?? raw.Active ?? true),
    enrollmentDate: (raw.enrollmentDate ?? raw.EnrollmentDate ?? undefined) as string | undefined,
    imagePath: (raw.imagePath ?? raw.ImagePath ?? undefined) as string | undefined,
  };
}
